/**
 * What the Plant Builder says it will make, and what a provision or a template install made, by whether the org gets a
 * gateway and its tags for each machine (orgGetsPlantGateways, lib/org/simulates). A real plant gets its machines and
 * how they connect, and no gateway or tag: nothing would ever feed them, because an import, a real gateway and manual
 * entry each write through their own (platform-core plants_generate.go and plant_templates.go, 2026-10-26). After a
 * provision the counts are the server's, and a real plant's answer carries a data_note the page shows as it is.
 *
 * The words follow docs/design/2026-10-23_writing-standard.md: plain sentences, and no dash.
 */
import type { InstallResult } from "@/lib/api/twin-plant-templates";
import type { ProvisionResult } from "@/lib/api/twin-plants";

/** Where a real plant's data comes from, the page's half of the server's data_note. */
export const REAL_PLANT_DATA_LINE = "Its data comes later, from an import, a gateway or manual entry.";

/** The "Create plant" button's line: what a provision will write in this org. */
export function plantBuilderPreviewLine(plantGateways: boolean): string {
  return plantGateways
    ? "Provision this plant: its machines and how they connect, a gateway for each machine, and its live tags."
    : `Provision this plant: its machines and how they connect. ${REAL_PLANT_DATA_LINE}`;
}

/** The Plant Builder page's description under its title. */
export function plantBuilderDescription(plantGateways: boolean): string {
  return plantGateways
    ? "Pick your metal and answer a few questions. We lay out your foundry and wire it for live data. The canvas updates as you go."
    : "Pick your metal and answer a few questions. We lay out your foundry's machines and how they connect. The canvas updates as you go.";
}

function counted(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** The toast after a provision, from the server's answer. */
export function provisionedMessage(r: ProvisionResult): string {
  const stations = counted(r.entities, "station", "stations");
  if (r.data_note) {
    return `Plant created with ${stations} and ${counted(r.connections, "connection", "connections")}. ${r.data_note}`;
  }
  return `Plant created with ${stations}, ${counted(r.gateways, "gateway", "gateways")} and ${counted(r.tags, "wired tag", "wired tags")}.`;
}

/** The toast after a template install, from the server's answer. */
export function installedMessage(r: InstallResult): string {
  const stations = counted(r.entities, "station", "stations");
  if (r.data_note) {
    return `Plant installed with ${stations} and ${counted(r.connections, "connection", "connections")}. ${r.data_note}`;
  }
  return `Plant installed with ${stations} and ${counted(r.tags, "wired tag", "wired tags")}.`;
}
